// "Parts" — the parts desk's home. Non-paint parts value by damage section
// (donut, <3% folded into Other; slices reuse the section filter) and by named
// component (bars with "$ | lines" labels), plus close-rate-adjusted expected
// demand and OEM/aftermarket mix.

import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, PieChart, Pie, LabelList } from "recharts"
import { ChapterSection, ChartCard, StatTile, BarTip, DonutTip, PillTabs, Highlight } from "../room-chrome"
import type { HighlightAction, HighlightFinding } from "../derive"
import { AXIS_TICK, CAT, fmt$, fmt$full } from "../room-model"
import type { PartsBreakdown } from "../derive"

export function PartsChapter({ parts, view, onSetView, highlight, onHighlight, section, onToggleSection, closeRate, adasDollars, rangeLabel }: {
  parts: PartsBreakdown
  view: string
  onSetView: (v: string) => void
  highlight?: HighlightFinding
  onHighlight: (a: HighlightAction) => void
  section: string
  onToggleSection: (key: string) => void
  closeRate: number
  adasDollars: number
  rangeLabel: string
}) {
  const expected = parts.totalValue * (closeRate / 100)
  const isComponent = view === "component"

  const sectionData = parts.bySection.map(s => ({
    ...s, dim: section !== "" && s.key !== "__other__" && section !== s.key,
  }))

  return (
    <ChapterSection id="parts" eyebrow="Repair mix" title="Parts breakdown" audience="For the parts desk">
      <ChartCard title="Non-paint parts value" sub={`Across the identified pipeline · ${rangeLabel}`}>
        {({ expanded }) => {
          const components = (expanded ? parts.byComponent : parts.byComponent.slice(0, 14)).map(c => ({
            ...c, barLabel: `${fmt$(c.value)}  |  ${c.lines} lines`, tip: `${fmt$full(c.value)} · ${c.lines} part line${c.lines !== 1 ? "s" : ""}`,
          }))
          const maxVal = Math.max(...components.map(c => c.value), 1)
          return (
            <>
              {/* view tabs + stats */}
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <PillTabs size="sm" ariaLabel="Parts breakdown view"
                  items={[{ key: "section", label: "Section" }, { key: "component", label: "Component" }]}
                  active={view} onSelect={onSetView} />
                {highlight && <Highlight text={highlight.text} onClick={highlight.action ? () => onHighlight(highlight.action!) : undefined} />}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
                <StatTile label="Total parts value" value={fmt$full(parts.totalValue)} />
                <StatTile label="Total part lines" value={parts.totalLines.toLocaleString()} />
                <StatTile label="Expected to convert" value={fmt$(expected)} sub={`at your ${closeRate}% close rate`} tone="mint" />
                <StatTile label="OEM / aftermarket" value={`${parts.oemPct}% OEM`} sub={`${100 - parts.oemPct}% aftermarket by $`} />
                <StatTile label="ADAS routed to service" value={fmt$(adasDollars)} sub="calibrations back to the dealership" tone="mint" />
              </div>

              {parts.totalValue === 0 ? (
                <p className="h-[220px] grid place-items-center text-sm text-[#8B897E]">No parts-level data on these estimates yet.</p>
              ) : isComponent ? (
                <div style={{ height: components.length * (expanded ? 30 : 24) + 24 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={components} layout="vertical" margin={{ left: 8, right: 132 }}>
                      <XAxis type="number" hide domain={[0, maxVal * 1.02]} />
                      <YAxis type="category" dataKey="name" width={150} tick={AXIS_TICK} tickLine={false} axisLine={false} />
                      <Tooltip content={<BarTip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                      <Bar dataKey="value" fill={CAT.brand} radius={[0, 3, 3, 0]} maxBarSize={16}>
                        <LabelList dataKey="barLabel" position="right" style={{ fontSize: 10, fill: "#52525b", fontVariantNumeric: "tabular-nums" }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex items-center gap-6 flex-wrap">
                  <div className="relative shrink-0" style={{ height: expanded ? 300 : 200, width: expanded ? 300 : 200 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={sectionData} dataKey="value" nameKey="name" innerRadius="62%" outerRadius="95%" paddingAngle={2} stroke="#fff" strokeWidth={2}
                          className="cursor-pointer"
                          onClick={(e: { key?: string }) => e?.key && e.key !== "__other__" && onToggleSection(e.key)}>
                          {sectionData.map(s => <Cell key={s.key} fill={s.color} opacity={s.dim ? 0.3 : 1} />)}
                        </Pie>
                        <Tooltip content={<DonutTip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 grid place-items-center pointer-events-none">
                      <div className="text-center">
                        <p className="text-lg font-bold text-zinc-900 tabular-nums leading-none">{fmt$(parts.totalValue)}</p>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#8B897E] mt-1.5">Parts value</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 min-w-[200px] space-y-1.5">
                    {sectionData.map(s => (
                      <button key={s.key} disabled={s.key === "__other__"}
                        onClick={() => s.key !== "__other__" && onToggleSection(s.key)}
                        className="w-full flex items-center gap-2.5 text-left py-1 disabled:cursor-default group/row">
                        <span className="size-2.5 rounded-[3px] shrink-0" style={{ background: s.color, opacity: s.dim ? 0.4 : 1 }} />
                        <span className="text-[13px] text-zinc-700 flex-1 truncate group-hover/row:text-zinc-900">{s.name}</span>
                        <span className="text-[13px] font-semibold tabular-nums text-zinc-900">{fmt$(s.value)}</span>
                        <span className="text-[11px] text-[#8B897E] tabular-nums w-14 text-right">{s.lines} lines</span>
                      </button>
                    ))}
                    {parts.otherNote && <p className="text-[11px] text-[#8B897E] pt-1">{parts.otherNote}</p>}
                  </div>
                </div>
              )}
              <p className="text-[11px] text-[#8B897E] mt-3">
                {isComponent ? "Click Section to see click-to-filter panels · " : "Click a section slice to filter the whole room · "}
                parts value reconciles with the Sales-by-category Parts slice.
              </p>
            </>
          )
        }}
      </ChartCard>
    </ChapterSection>
  )
}
